/**
 * By default, Remix will handle hydrating your app on the client for you.
 * You are free to delete this file if you'd like to, but if you ever want it revealed again, you can run `npx remix reveal` ✨
 * For more information, see https://remix.run/file-conventions/entry.client
 */

import { RemixBrowser } from "@remix-run/react";
import { startTransition, StrictMode, type ErrorInfo } from "react";
import { hydrateRoot } from "react-dom/client";
import {
  installBrowserErrorCapture,
  reportBrowserError,
} from "@core/shared/services/error-reporting/browser-error-reporter.service";
import { installSessionStorageFallback } from "@core/shared/utils/install-session-storage-fallback";
import { upgradeInsecurePageToHttps } from "@framework/auth/browser-environment";

installBrowserErrorCapture();
installSessionStorageFallback();
// Some tenant hosts still answer on plain http:// without redirecting; there
// the browser hides WebCrypto and refuses Secure cookies, so sign-in can never
// work. Move to https:// once a probe proves the host serves it.
void upgradeInsecurePageToHttps(window);

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <RemixBrowser />
    </StrictMode>,
    {
      onRecoverableError(error: unknown, errorInfo: ErrorInfo) {
        try {
          reportBrowserError({
            message: safeHydrationErrorMessage(error),
            stack: safeHydrationErrorStack(error),
            source: "hydration",
            additionalData:
              typeof errorInfo?.componentStack === "string"
                ? { componentStack: errorInfo.componentStack }
                : undefined,
          }).catch(() => undefined);
        } catch {
          // Hydration error reporting must never throw back into React's
          // recoverable-error path.
        }
      },
    },
  );
});

/** Extract a safe hydration error message without coercing a hostile value. */
function safeHydrationErrorMessage(error: unknown): string {
  try {
    if (error instanceof Error && typeof error.message === "string") {
      return error.message;
    }

    if (error === null || error === undefined) return "Hydration error";

    return String(error);
  } catch {
    return "Hydration error";
  }
}

/** Extract a safe hydration error stack, if present. */
function safeHydrationErrorStack(error: unknown): string | undefined {
  try {
    if (error instanceof Error && typeof error.stack === "string") {
      return error.stack;
    }

    return undefined;
  } catch {
    return undefined;
  }
}
